/**
 * Colour tokens — theme step 1 (2026-10-05) moved every colour onto tokens; theme step 2 (2026-10-05) adds the light
 * palette. Dark is the default and its values are unchanged (frozen in scripts/theme-token-check.mjs).
 *
 * This file is the source of truth for colour VALUES. Every entry `'<name>': '#RRGGBB'` is mirrored 1:1 in
 * src/styles/globals.css as two custom properties:
 *   --c-<name>      #RRGGBB   opaque use:  var(--c-<name>)
 *   --c-<name>-rgb  R G B     alpha use:   rgb(var(--c-<name>-rgb) / <alpha>)   (and Tailwind <alpha-value>)
 * `paletteDark` → `:root, [data-theme="dark"]`; `paletteLight` → `:root[data-theme="light"]`;
 * `paletteGraphite` → `:root[data-theme="graphite"]` (theme step 3, 2026-10-06).
 * scripts/theme-token-check.mjs asserts the tables and the stylesheet stay identical and that every token is defined
 * in every theme.
 *
 * Use the CSS variables everywhere CSS resolves them (className arbitrary values, style objects, <style>, .css) — those
 * follow the theme with no JS. Use the palette ONLY where a var() cannot work: canvas 2D, lightweight-charts and
 * vis-network options, three.js, framer-motion keyframes, SVG presentation attributes, React Flow marker colours and
 * concatenated strings. Those sinks read the CURRENT theme through `usePalette()` (src/theme/theme.ts) — or
 * `paletteFor(theme)` / `paletteRgba(name, alpha, theme)` — and re-render / applyOptions when the theme changes.
 */
export const paletteDark = {
  // Command surfaces + text (tailwind cmd-* / saa-*)
  'bg': '#050505', // page background (cmd-bg)
  'surface': '#0A0D10', // app surface (cmd-surface, saa-dark)
  'panel': '#111318', // panel (cmd-panel, saa-dark-light)
  'panel-alt': '#0D1117', // panel header (cmd-panelAlt)
  'panel-mute': '#090C11', // inset panel (cmd-panelMute)
  'border': '#1F2937', // hairline border (cmd-border)
  'text': '#E5E7EB', // primary text (cmd-text)
  'text-mute': '#94A3B8', // secondary text, grid lines at low alpha (cmd-textMute)
  'text-dim': '#64748B', // tertiary text (cmd-textDim)
  'accent': '#93A4B8', // cmd-accent / saa-gold (steel accent)
  'accent-dark': '#6F8094', // saa-gold-dark
  'active': '#8D97A1', // steel active state, focus ring (cmd-active)
  'safe': '#6B7351', // safe / positive state (cmd-safe)
  'warning': '#7F6842', // warning state (cmd-warning)
  'critical': '#8C5454', // critical state (cmd-critical)
  'ink': '#FFFFFF', // white ink: hairlines and fills at low alpha over dark surfaces
  'black': '#000000', // black scrim
  'zinc-800': '#27272A', // tailwind DEFAULT border colour (zinc-800/60)
  // Gold / brand accent family
  'gold': '#C9A858', // brand gold — primary action, gateway accent (G.GOLD)
  'gold-bright': '#D1A954', // bright gold highlight
  'amber': '#D1A65E', // amber warning banner / warn tone
  'gold-muted': '#B0925C', // muted gold (VaR bands, legends)
  'bronze': '#9D8051', // bronze status text
  'gold-soft': '#C9A961', // soft gold
  'gold-warn': '#C29A5A', // warn tone text (identity pages)
  'gold-light': '#E2C47A', // light gold text
  'gold-deep': '#C9A227', // deep gold line
  'gold-pale': '#E5D6A8', // pale gold hover text
  'gold-dim': '#9A7A45', // dim gold
  // Status tints
  'safe-bright': '#7E875F', // positive figure text
  'safe-text': '#D8DEC8', // text on safe tint
  'critical-text': '#D9B1B1', // text on critical tint
  'ok': '#7FB08A', // ok tone (account status)
  'ok-text': '#9CC5A6', // ok tone text (identity pages)
  'alert-red': '#FF3333', // alert red (badges, error tints)
  'alert-amber': '#FFB800', // alert amber (badges, warning tints)
  // Gateway + terminal neutrals
  'text-body': '#A3AAB5', // muted body text (gateway BODY)
  'text-soft': '#8A919C', // soft secondary text
  'text-meta': '#7F8A99', // meta text (gateway META)
  'text-label': '#8B95A3', // form label text (login/register)
  'text-strong': '#B7C0CA', // emphasised secondary text
  'glass': '#1A1921', // gateway glass card (alpha 0.72)
  'panel-deep': '#0E0F12', // deep panel
  'panel-black': '#0A0A0C', // near-black card
  'panel-raised': '#111114', // raised near-black card
  'panel-ink': '#0B0C0E', // ink-black panel / text on gold
  // Tailwind default-scale colours written as hex literals
  'gray-100': '#F3F4F6', // tailwind gray-100
  'gray-300': '#D1D5DB', // tailwind gray-300
  'gray-400': '#9CA3AF', // tailwind gray-400
  'gray-500': '#6B7280', // tailwind gray-500
  'gray-600': '#4B5563', // tailwind gray-600
  'gray-700': '#374151', // tailwind gray-700
  'slate-300': '#CBD5E1', // tailwind slate-300
  'slate-200': '#E2E8F0', // tailwind slate-200
  'slate-600': '#475569', // tailwind slate-600
  'slate-700': '#334155', // tailwind slate-700
  'slate-900': '#0F172A', // tailwind slate-900
  'green-500': '#22C55E', // tailwind green-500
  'emerald-500': '#10B981', // tailwind emerald-500
  'emerald-400': '#34D399', // tailwind emerald-400
  'red-500': '#EF4444', // tailwind red-500
  'red-400': '#F87171', // tailwind red-400
  'red-300': '#FCA5A5', // tailwind red-300
  'blue-500': '#3B82F6', // tailwind blue-500
  'violet-500': '#8B5CF6', // tailwind violet-500
  'yellow-500': '#EAB308', // tailwind yellow-500
  'amber-400': '#FBBF24', // tailwind amber-400
  'amber-600': '#D97706', // tailwind amber-600
  // Tailwind built-in palette classes (text-red-400, bg-amber-900/25, …) — routed through tokens in theme step 2
  'amber-200': '#FDE68A', // tailwind amber-200 (built-in class, theme step 2)
  'amber-300': '#FCD34D', // tailwind amber-300 (built-in class, theme step 2)
  'amber-500': '#F59E0B', // tailwind amber-500 (built-in class, theme step 2)
  'amber-700': '#B45309', // tailwind amber-700 (built-in class, theme step 2)
  'amber-800': '#92400E', // tailwind amber-800 (built-in class, theme step 2)
  'amber-900': '#78350F', // tailwind amber-900 (built-in class, theme step 2)
  'amber-950': '#451A03', // tailwind amber-950 (built-in class, theme step 2)
  'blue-400': '#60A5FA', // tailwind blue-400 (built-in class, theme step 2)
  'blue-950': '#172554', // tailwind blue-950 (built-in class, theme step 2)
  'emerald-300': '#6EE7B7', // tailwind emerald-300 (built-in class, theme step 2)
  'emerald-700': '#047857', // tailwind emerald-700 (built-in class, theme step 2)
  'emerald-800': '#065F46', // tailwind emerald-800 (built-in class, theme step 2)
  'emerald-900': '#064E3B', // tailwind emerald-900 (built-in class, theme step 2)
  'emerald-950': '#022C22', // tailwind emerald-950 (built-in class, theme step 2)
  'gray-800': '#1F2937', // tailwind gray-800 (built-in class, theme step 2)
  'green-400': '#4ADE80', // tailwind green-400 (built-in class, theme step 2)
  'indigo-300': '#A5B4FC', // tailwind indigo-300 (built-in class, theme step 2)
  'indigo-800': '#3730A3', // tailwind indigo-800 (built-in class, theme step 2)
  'indigo-900': '#312E81', // tailwind indigo-900 (built-in class, theme step 2)
  'orange-400': '#FB923C', // tailwind orange-400 (built-in class, theme step 2)
  'purple-300': '#D8B4FE', // tailwind purple-300 (built-in class, theme step 2)
  'purple-400': '#C084FC', // tailwind purple-400 (built-in class, theme step 2)
  'purple-500': '#A855F7', // tailwind purple-500 (built-in class, theme step 2)
  'purple-800': '#6B21A8', // tailwind purple-800 (built-in class, theme step 2)
  'purple-900': '#581C87', // tailwind purple-900 (built-in class, theme step 2)
  'purple-950': '#3B0764', // tailwind purple-950 (built-in class, theme step 2)
  'red-200': '#FECACA', // tailwind red-200 (built-in class, theme step 2)
  'red-600': '#DC2626', // tailwind red-600 (built-in class, theme step 2)
  'red-800': '#991B1B', // tailwind red-800 (built-in class, theme step 2)
  'red-900': '#7F1D1D', // tailwind red-900 (built-in class, theme step 2)
  'red-950': '#450A0A', // tailwind red-950 (built-in class, theme step 2)
  'sky-300': '#7DD3FC', // tailwind sky-300 (built-in class, theme step 2)
  'sky-500': '#0EA5E9', // tailwind sky-500 (built-in class, theme step 2)
  'slate-800': '#1E293B', // tailwind slate-800 (built-in class, theme step 2)
  'yellow-300': '#FDE047', // tailwind yellow-300 (built-in class, theme step 2)
  'yellow-400': '#FACC15', // tailwind yellow-400 (built-in class, theme step 2)
  'yellow-950': '#422006', // tailwind yellow-950 (built-in class, theme step 2)
  // Value-named (role unclear / one-off)
  'hex-041015': '#041015',
  'hex-0b0b0e': '#0B0B0E',
  'hex-0e0e13': '#0E0E13',
  'hex-0f0f11': '#0F0F11',
  'hex-111418': '#111418',
  'hex-14131a': '#14131A',
  'hex-1a1a1e': '#1A1A1E',
  'hex-1a1b1e': '#1A1B1E',
  'hex-1a1d24': '#1A1D24',
  'hex-2a2b2e': '#2A2B2E',
  'hex-2d3748': '#2D3748',
  'hex-2dff5a': '#2DFF5A',
  'hex-3a3b3e': '#3A3B3E',
  'hex-3a414e': '#3A414E',
  'hex-3d4552': '#3D4552',
  'hex-3f4653': '#3F4653',
  'hex-414855': '#414855',
  'hex-434a57': '#434A57',
  'hex-454c58': '#454C58',
  'hex-46515c': '#46515C',
  'hex-4a5a3a': '#4A5A3A',
  'hex-56606e': '#56606E',
  'hex-5a6a3a': '#5A6A3A',
  'hex-608cbe': '#608CBE',
  'hex-6a7a4a': '#6A7A4A',
  'hex-6b7481': '#6B7481',
  'hex-6c7380': '#6C7380',
  'hex-707784': '#707784',
  'hex-737a87': '#737A87',
  'hex-767d8a': '#767D8A',
  'hex-789664': '#789664',
  'hex-79808c': '#79808C',
  'hex-7a8a5a': '#7A8A5A',
  'hex-7a8a9a': '#7A8A9A',
  'hex-7c838f': '#7C838F',
  'hex-8a8a8a': '#8A8A8A',
  'hex-8a9a6a': '#8A9A6A',
  'hex-9d2b2b': '#9D2B2B',
  'hex-a1a1a1': '#A1A1A1',
  'hex-a3b07a': '#A3B07A',
  'hex-a88f47': '#A88F47',
  'hex-ac7575': '#AC7575',
  'hex-b05c5c': '#B05C5C',
  'hex-b4beca': '#B4BECA',
  'hex-b8941f': '#B8941F',
  'hex-c0392b': '#C0392B',
  'hex-c8a45b': '#C8A45B',
  'hex-c8a86b': '#C8A86B',
  'hex-c9a258': '#C9A258',
  'hex-c9b27a': '#C9B27A',
  'hex-c9cfd8': '#C9CFD8',
  'hex-c9d0d8': '#C9D0D8',
  'hex-d08a8a': '#D08A8A',
  'hex-d7dccb': '#D7DCCB',
  'hex-d9b45a': '#D9B45A',
  'hex-d9b971': '#D9B971',
  'hex-e5e1d4': '#E5E1D4',
  'hex-e5e4e2': '#E5E4E2',
  'hex-e8c468': '#E8C468',
  'hex-e8e8e8': '#E8E8E8',
  'hex-ffd700': '#FFD700',
} as const;

export type PaletteToken = keyof typeof paletteDark;
export type ThemeName = 'dark' | 'light' | 'graphite';

/** Light palette (theme step 2). Values chosen by role (surface / text / status / tint) — see
 *  docs/theme-step2-light-2026-10-05.md for the WCAG contrast of every text and status token. */
export const paletteLight: Record<PaletteToken, string> = {
  // Command surfaces + text (tailwind cmd-* / saa-*)
  'bg': '#FFFFFF',
  'surface': '#FFFFFF',
  'panel': '#FFFFFF',
  'panel-alt': '#F4F6F8',
  'panel-mute': '#FAFBFC',
  'border': '#C9CED6',
  'text': '#05070A',
  'text-mute': '#1F2733',
  'text-dim': '#3E4756',
  'accent': '#1D3B5C',
  'accent-dark': '#3B4A5C',
  'active': '#0F1A2A',
  'safe': '#1E6B2E',
  'warning': '#8A5300',
  'critical': '#B3261E',
  'ink': '#0F172A',
  'black': '#0F172A',
  'zinc-800': '#C9CDCB',
  // Gold / brand accent family
  'gold': '#7A5700',
  'gold-bright': '#866918',
  'amber': '#9A5800',
  'gold-muted': '#6A5530',
  'bronze': '#7A5F2E',
  'gold-soft': '#765C16',
  'gold-warn': '#8A5F14',
  'gold-light': '#7A5E16',
  'gold-deep': '#7F6410',
  'gold-pale': '#5E4A12',
  'gold-dim': '#A8894E',
  // Status tints
  'safe-bright': '#4A6418',
  'safe-text': '#3A4520',
  'critical-text': '#7F1D1D',
  'ok': '#2F6B3E',
  'ok-text': '#2F6B3E',
  'alert-red': '#C81E1E',
  'alert-amber': '#B7791F',
  // Gateway + terminal neutrals
  'text-body': '#1F2733',
  'text-soft': '#2E3744',
  'text-meta': '#565E6B',
  'text-label': '#4B5563',
  'text-strong': '#1F2937',
  'glass': '#FFFFFF',
  'panel-deep': '#FFFFFF',
  'panel-black': '#FFFFFF',
  'panel-raised': '#F6F7F7',
  'panel-ink': '#FFFFFF',
  // Tailwind default-scale colours written as hex literals
  'gray-100': '#111827',
  'gray-300': '#1F2937',
  'gray-400': '#374151',
  'gray-500': '#4B5563',
  'gray-600': '#5F6672',
  'gray-700': '#646B77',
  'slate-300': '#334155',
  'slate-200': '#1E293B',
  'slate-600': '#5F6672',
  'slate-700': '#C4C9CF',
  'slate-900': '#E5E7EB',
  'green-500': '#15803D',
  'emerald-500': '#047857',
  'emerald-400': '#047857',
  'red-500': '#C81E1E',
  'red-400': '#A51A1A',
  'red-300': '#991B1B',
  'blue-500': '#2563EB',
  'violet-500': '#7C3AED',
  'yellow-500': '#856500',
  'amber-400': '#78350F',
  'amber-600': '#B45309',
  // Tailwind built-in palette classes (text-red-400, bg-amber-900/25, …) — routed through tokens in theme step 2
  'amber-200': '#78350F',
  'amber-300': '#78350F',
  'amber-500': '#D97706',
  'amber-700': '#FCD34D',
  'amber-800': '#FDE68A',
  'amber-900': '#FEF3C7',
  'amber-950': '#FFFBEB',
  'blue-400': '#1E40AF',
  'blue-950': '#EFF6FF',
  'emerald-300': '#065F46',
  'emerald-700': '#6EE7B7',
  'emerald-800': '#A7F3D0',
  'emerald-900': '#D1FAE5',
  'emerald-950': '#ECFDF5',
  'gray-800': '#E5E7EB',
  'green-400': '#166534',
  'indigo-300': '#3730A3',
  'indigo-800': '#C7D2FE',
  'indigo-900': '#E0E7FF',
  'orange-400': '#9A3412',
  'purple-300': '#6B21A8',
  'purple-400': '#6B21A8',
  'purple-500': '#9333EA',
  'purple-800': '#E9D5FF',
  'purple-900': '#F3E8FF',
  'purple-950': '#FAF5FF',
  'red-200': '#991B1B',
  'red-600': '#DC2626',
  'red-800': '#FECACA',
  'red-900': '#FEE2E2',
  'red-950': '#FEF2F2',
  'sky-300': '#075985',
  'sky-500': '#0284C7',
  'slate-800': '#E2E8F0',
  'yellow-300': '#854D0E',
  'yellow-400': '#854D0E',
  'yellow-950': '#FEFCE8',
  // Value-named (role unclear / one-off)
  'hex-041015': '#FFFFFF',
  'hex-0b0b0e': '#FFFFFF',
  'hex-0e0e13': '#FFFFFF',
  'hex-0f0f11': '#FFFFFF',
  'hex-111418': '#F3F4F6',
  'hex-14131a': '#FFFFFF',
  'hex-1a1a1e': '#EEF0F1',
  'hex-1a1b1e': '#F3F4F6',
  'hex-1a1d24': '#F3F4F6',
  'hex-2a2b2e': '#D8DBD9',
  'hex-2d3748': '#CBD5E1',
  'hex-2dff5a': '#15803D',
  'hex-3a3b3e': '#E5E7EB',
  'hex-3a414e': '#C9CED5',
  'hex-3d4552': '#C6CBD2',
  'hex-3f4653': '#C3C8CF',
  'hex-414855': '#C0C5CC',
  'hex-434a57': '#BDC2C9',
  'hex-454c58': '#BABFC6',
  'hex-46515c': '#B0B8C1',
  'hex-4a5a3a': '#B8C4A4',
  'hex-56606e': '#4B5563',
  'hex-5a6a3a': '#8A9A6A',
  'hex-608cbe': '#2F5F9E',
  'hex-6a7a4a': '#6A7A4A',
  'hex-6b7481': '#7D8591',
  'hex-6c7380': '#7D8591',
  'hex-707784': '#7A828E',
  'hex-737a87': '#78808C',
  'hex-767d8a': '#767E8A',
  'hex-789664': '#4E6B3A',
  'hex-79808c': '#747C88',
  'hex-7a8a5a': '#5A6A3A',
  'hex-7a8a9a': '#55667A',
  'hex-7c838f': '#727A86',
  'hex-8a8a8a': '#525252',
  'hex-8a9a6a': '#4A5A2A',
  'hex-9d2b2b': '#B91C1C',
  'hex-a1a1a1': '#4B5563',
  'hex-a3b07a': '#4A5A1E',
  'hex-a88f47': '#7A5E16',
  'hex-ac7575': '#9B2C2C',
  'hex-b05c5c': '#9B2C2C',
  'hex-b4beca': '#64748B',
  'hex-b8941f': '#8A6D1F',
  'hex-c0392b': '#B91C1C',
  'hex-c8a45b': '#8A6D1F',
  'hex-c8a86b': '#C8A86B',
  'hex-c9a258': '#8A6D1F',
  'hex-c9b27a': '#6F5614',
  'hex-c9cfd8': '#1F2937',
  'hex-c9d0d8': '#1F2937',
  'hex-d08a8a': '#9B2C2C',
  'hex-d7dccb': '#3A4520',
  'hex-d9b45a': '#765C16',
  'hex-d9b971': '#8A6D1F',
  'hex-e5e1d4': '#1F2937',
  'hex-e5e4e2': '#1F2937',
  'hex-e8c468': '#8A6D1F',
  'hex-e8e8e8': '#111827',
  'hex-ffd700': '#A16207',
};

/** Graphite palette (theme step 3, 2026-10-06, admin-only preview). Ground #1A1D23 (RGB 26·29·35) — dark graphite with
 *  a slight cold blue bias. Surfaces/cards/borders are cool-graphite steps around the ground (same blue bias); text and
 *  status tokens keep their dark value unless it fails WCAG AA 4.5:1 on a graphite surface (or on its own tint / at its
 *  translucent alpha), in which case only the HSL lightness was raised to the first passing step. Checked by
 *  scripts/theme-token-check.mjs. */
export const paletteGraphite: Record<PaletteToken, string> = {
  // Command surfaces + text (tailwind cmd-* / saa-*)
  'bg': '#101216',
  'surface': '#1A1D23',
  'panel': '#1A1D23',
  'panel-alt': '#1A1D23',
  'panel-mute': '#181B20',
  'border': '#363C47',
  'text': '#E5E7EB',
  'text-mute': '#94A3B8',
  'text-dim': '#8392A6',
  'accent': '#93A4B8',
  'accent-dark': '#6F8094',
  'active': '#8D97A1',
  'safe': '#A0A884',
  'warning': '#B89F77',
  'critical': '#C8A4A4',
  'ink': '#FFFFFF',
  'black': '#000000',
  'zinc-800': '#30353E',
  // Gold / brand accent family
  'gold': '#CDAE64',
  'gold-bright': '#D1A954',
  'amber': '#D1A65E',
  'gold-muted': '#B99F6F',
  'bronze': '#AF9263',
  'gold-soft': '#C9A961',
  'gold-warn': '#C29A5A',
  'gold-light': '#E2C47A',
  'gold-deep': '#C9A227',
  'gold-pale': '#E5D6A8',
  'gold-dim': '#9A7A45',
  // Status tints
  'safe-bright': '#A0A883',
  'safe-text': '#D8DEC8',
  'critical-text': '#D9B1B1',
  'ok': '#7FB08A',
  'ok-text': '#9CC5A6',
  'alert-red': '#FF3333',
  'alert-amber': '#FFB800',
  // Gateway + terminal neutrals
  'text-body': '#A3AAB5',
  'text-soft': '#8A919C',
  'text-meta': '#86909F',
  'text-label': '#8B95A3',
  'text-strong': '#B7C0CA',
  'glass': '#272B33',
  'panel-deep': '#1A1D23',
  'panel-black': '#1A1D23',
  'panel-raised': '#1A1D23',
  'panel-ink': '#1A1D23',
  // Tailwind default-scale colours written as hex literals
  'gray-100': '#F3F4F6',
  'gray-300': '#D1D5DB',
  'gray-400': '#9CA3AF',
  'gray-500': '#89909C',
  'gray-600': '#8592A3',
  'gray-700': '#8191AA',
  'slate-300': '#CBD5E1',
  'slate-200': '#E2E8F0',
  'slate-600': '#8192AA',
  'slate-700': '#3A4253',
  'slate-900': '#1E2430',
  'green-500': '#22C55E',
  'emerald-500': '#10B981',
  'emerald-400': '#34D399',
  'red-500': '#F15E5E',
  'red-400': '#F98989',
  'red-300': '#FCA5A5',
  'blue-500': '#4F8EF7',
  'violet-500': '#A079F8',
  'yellow-500': '#EAB308',
  'amber-400': '#FBBF24',
  'amber-600': '#D97706',
  // Tailwind built-in palette classes (text-red-400, bg-amber-900/25, …) — routed through tokens in theme step 2
  'amber-200': '#FDE68A',
  'amber-300': '#FCD34D',
  'amber-500': '#F59E0B',
  'amber-700': '#B45309',
  'amber-800': '#92400E',
  'amber-900': '#78350F',
  'amber-950': '#451A03',
  'blue-400': '#6CACFA',
  'blue-950': '#172554',
  'emerald-300': '#6EE7B7',
  'emerald-700': '#047857',
  'emerald-800': '#065F46',
  'emerald-900': '#064E3B',
  'emerald-950': '#022C22',
  'gray-800': '#2C323C',
  'green-400': '#4ADE80',
  'indigo-300': '#A5B4FC',
  'indigo-800': '#3730A3',
  'indigo-900': '#312E81',
  'orange-400': '#FB923C',
  'purple-300': '#D8B4FE',
  'purple-400': '#C893FC',
  'purple-500': '#A855F7',
  'purple-800': '#6B21A8',
  'purple-900': '#581C87',
  'purple-950': '#3B0764',
  'red-200': '#FECACA',
  'red-600': '#DC2626',
  'red-800': '#991B1B',
  'red-900': '#7F1D1D',
  'red-950': '#450A0A',
  'sky-300': '#7DD3FC',
  'sky-500': '#0EA5E9',
  'slate-800': '#2A3140',
  'yellow-300': '#FDE047',
  'yellow-400': '#FACC15',
  'yellow-950': '#422006',
  // Value-named (role unclear / one-off)
  'hex-041015': '#18222A',
  'hex-0b0b0e': '#1C1F25',
  'hex-0e0e13': '#1E2128',
  'hex-0f0f11': '#1E2127',
  'hex-111418': '#21252C',
  'hex-14131a': '#22242C',
  'hex-1a1a1e': '#25282F',
  'hex-1a1b1e': '#25282F',
  'hex-1a1d24': '#262A32',
  'hex-2a2b2e': '#33363D',
  'hex-2d3748': '#36404F',
  'hex-2dff5a': '#2DFF5A',
  'hex-3a3b3e': '#3A3B3E',
  'hex-3a414e': '#3A414E',
  'hex-3d4552': '#3D4552',
  'hex-3f4653': '#3F4653',
  'hex-414855': '#414855',
  'hex-434a57': '#434A57',
  'hex-454c58': '#454C58',
  'hex-46515c': '#46515C',
  'hex-4a5a3a': '#4A5A3A',
  'hex-56606e': '#8792A1',
  'hex-5a6a3a': '#5A6A3A',
  'hex-608cbe': '#608CBE',
  'hex-6a7a4a': '#6A7A4A',
  'hex-6b7481': '#6B7481',
  'hex-6c7380': '#6C7380',
  'hex-707784': '#707784',
  'hex-737a87': '#737A87',
  'hex-767d8a': '#767D8A',
  'hex-789664': '#789664',
  'hex-79808c': '#79808C',
  'hex-7a8a5a': '#7A8A5A',
  'hex-7a8a9a': '#7A8A9A',
  'hex-7c838f': '#7C838F',
  'hex-8a8a8a': '#909090',
  'hex-8a9a6a': '#8A9A6A',
  'hex-9d2b2b': '#9D2B2B',
  'hex-a1a1a1': '#A1A1A1',
  'hex-a3b07a': '#A3B07A',
  'hex-a88f47': '#A88F47',
  'hex-ac7575': '#B58383',
  'hex-b05c5c': '#C48585',
  'hex-b4beca': '#B4BECA',
  'hex-b8941f': '#B8941F',
  'hex-c0392b': '#C0392B',
  'hex-c8a45b': '#C8A45B',
  'hex-c8a86b': '#C8A86B',
  'hex-c9a258': '#C9A258',
  'hex-c9b27a': '#C9B27A',
  'hex-c9cfd8': '#C9CFD8',
  'hex-c9d0d8': '#C9D0D8',
  'hex-d08a8a': '#D08A8A',
  'hex-d7dccb': '#D7DCCB',
  'hex-d9b45a': '#D9B45A',
  'hex-d9b971': '#D9B971',
  'hex-e5e1d4': '#E5E1D4',
  'hex-e5e4e2': '#E5E4E2',
  'hex-e8c468': '#E8C468',
  'hex-e8e8e8': '#E8E8E8',
  'hex-ffd700': '#FFD700',
};

/** Step-1 name, kept for non-rendering callers: the DARK palette. Rendering sinks use paletteFor(theme). */
export const palette = paletteDark;

export function paletteFor(theme: ThemeName): Record<PaletteToken, string> {
  if (theme === 'light') return paletteLight;
  if (theme === 'graphite') return paletteGraphite;
  return paletteDark;
}

/** `rgba(r,g,b,alpha)` for a token — the literal form for JS sinks that need an alpha (canvas, charts). */
export function paletteRgba(name: PaletteToken, alpha: string | number, theme: ThemeName = 'dark'): string {
  const h = paletteFor(theme)[name];
  return `rgba(${parseInt(h.slice(1, 3), 16)},${parseInt(h.slice(3, 5), 16)},${parseInt(h.slice(5, 7), 16)},${alpha})`;
}
